import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BasePetition, Category, Petition, NULL_BASE_PETITION } from '@ncessor/shared';
import { NcessorService } from '../services/ncessor.service';
import { PetitionEditDialog, PetitionEditData } from '../petition-editor/petition-edit-dialog';
import { MessageService } from '../services/message.service';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog';
import { Subject, Observable, of as observableOf, ReplaySubject, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { PetitionStateListComponent } from './petition-state-list/petition-state-list.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-category-content-page',
  templateUrl: './category-content-page.component.html',
  styleUrls: ['./category-content-page.scss'],
  imports: [MatIconButton, MatIcon, LoadingOverlayComponent, PetitionStateListComponent, AsyncPipe],
})
export class CategoryContentPageComponent implements OnInit {
  categoryId: string;
  category: Category | null = null;
  category$: Subject<Category | null> = new ReplaySubject();
  activePetitions$: Subject<Petition[]> = new Subject();
  pendingPetitions$: Subject<Petition[]> = new Subject();
  lapsedPetitions$: Subject<Petition[]> = new Subject();
  isLoadingActive = false;
  isLoadingPending = false;
  isLoadingLapsed = false;
  isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private ncessorService: NcessorService,
    private messageService: MessageService,
  ) {
    const catParam: string | null = this.route.snapshot.params['id'];
    this.categoryId = catParam || 'undefined';
  }

  ngOnInit(): void {
    if (this.categoryId && this.categoryId !== 'undefined') {
      this.ncessorService.getCategoryById(this.categoryId).subscribe(this.category$);
    } else {
      this.category$.next(null);
    }

    this.fetchPetitions();
  }

  onAddPetition() {
    const newPetition: BasePetition = {
      ...NULL_BASE_PETITION,
      category: this.categoryId ? this.categoryId : null,
    };
    this.openEditDialog(newPetition, true, (newPetition: Petition) => {
      this.ncessorService.addPetition(newPetition).subscribe((addedItem: Petition) => {
        this.messageService.logNormal(`Added new petition: ${JSON.stringify(addedItem)}`);
        this.fetchPetitions();
      });
    });
  }

  onEditPetition(petition: Petition) {
    this.openEditDialog(petition, false, (result) => {
      const editedPetition = {
        ...result,
        _id: petition._id,
      };
      this.ncessorService.updatePetition(editedPetition).subscribe((updatedItem: Petition) => {
        this.messageService.logNormal(`Updated petition: "${updatedItem.text}"`);
        this.fetchPetitions();
      });
    });
  }

  private openEditDialog(
    editPetition: BasePetition,
    isNew: boolean,
    callback: (petition: Petition) => void,
  ) {
    const dialogData: PetitionEditData = {
      editPetition,
      isNewPetition: isNew,
    };
    var dialogRef = this.dialog.open(PetitionEditDialog, {
      width: '30em',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result: Petition) => {
      if (!result) {
        this.messageService.logNormal(`Petition ${isNew ? 'add' : 'edit'} operation canceled.`);
        return;
      }
      callback(result);
    });
  }

  onDeletePetition(petition: Petition) {
    ConfirmDialog.openConfirmDialog(
      this.dialog,
      {
        title: 'Confirm Petition Deletion',
        bodyText: `Do you want to delete the petition, "${petition.text}"?`,
        confirmButtonLabel: 'Delete',
      },
      (answer) => {
        if (!answer) {
          this.messageService.logNormal(`Petition deletion canceled.`);
          return;
        }
        this.ncessorService.deletePetition(petition._id).subscribe((_) => {
          this.messageService.logNormal(`Deleted petition: "${petition.text}"`);
          this.fetchPetitions();
        });
      },
    );
  }

  private fetchPetitions() {
    this.isLoadingActive = true;
    this.isLoadingPending = true;
    this.isLoadingLapsed = true;
    // TODO: undefined catId causes all petitions to be fetched. Instead, change server to
    // treat catId of 'none' to mean petitions with undefined category.
    const catId = this.categoryId === 'undefined' ? undefined : this.categoryId;
    this.ncessorService.getPetitions({ category: catId, state: 'active' }).subscribe((result) => {
      this.activePetitions$.next(result);
      this.isLoadingActive = false;
      this.updateLoadingState();
    });
    this.ncessorService.getPetitions({ category: catId, state: 'pending' }).subscribe((result) => {
      this.pendingPetitions$.next(result);
      this.isLoadingPending = false;
      this.updateLoadingState();
    });
    this.ncessorService.getPetitions({ category: catId, state: 'lapsed' }).subscribe((result) => {
      this.lapsedPetitions$.next(result);
      this.isLoadingLapsed = false;
      this.updateLoadingState();
    });
  }

  get categoryName() {
    return this.category$.pipe(map((cat) => (cat === null ? 'Uncategorized' : cat.name)));
  }

  private updateLoadingState() {
    this.isLoading$.next(this.isLoadingActive || this.isLoadingPending || this.isLoadingLapsed);
  }
}
